'use client';

import FormInput from '@form/FormInput';
import {
    faCircleInfo,
    faLocationDot,
    faMagnifyingGlass,
    faPenToSquare,
    faTriangleExclamation,
    faXmark
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Blockquote, Button, Spinner, StyledInput } from '@ui';
import { cx } from 'class-variance-authority';
import { useApi } from 'hooks/useApi';
import useModalManager from 'hooks/useModalManager';
import { useEffect, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';

interface Props {
    label: string;
    name: string;
    required?: boolean;
    inputProps?: any;
}

const PLACES_BASE = 'places';

interface AddressComponent {
    types: string[];
    long_name: string;
    short_name: string;
}

interface Prediction {
    place_id: string;
    description?: string;
    types: string[];
    structured_formatting: {
        main_text: string;
        secondary_text: string;
    };
}

interface PlacesAutocompleteResponse {
    predictions?: RawPrediction[];
    status?: string;
}

interface RawPrediction {
    place_id?: string;
    description?: string;
    types?: string[];
    structured_formatting?: {
        main_text?: string;
        secondary_text?: string;
    };
}

const getAddressValidationMessage = (required: boolean, value: AddressValue | undefined) => {
    if (!required) return true;

    const address = value ?? {};
    const relevantValues = [address.street, address.streetnumber, address.postalcode, address.city];
    const hasAnyValue = relevantValues.some((field) => String(field ?? '').trim().length > 0);
    const hasCompleteAddress = relevantValues.every((field) => String(field ?? '').trim().length > 0);

    if (!hasAnyValue) return 'Bitte gebe eine gültige Adresse ein.';
    if (!hasCompleteAddress) return 'Bitte gebe eine vollständige Adresse an.';

    return true;
};

const getComponent = (type: string, components: AddressComponent[] = [], short = false): string => {
    const found = components.find((c) => c.types.includes(type));
    if (!found) return '';
    return short ? found.short_name : found.long_name;
};

const normalizePrediction = (prediction: RawPrediction): Prediction | null => {
    if (!prediction?.place_id) return null;

    const description = prediction.description?.trim() ?? '';
    const descriptionParts = description
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);

    const mainText = prediction.structured_formatting?.main_text?.trim() || descriptionParts[0] || description;
    const secondaryText =
        prediction.structured_formatting?.secondary_text?.trim() || descriptionParts.slice(1).join(', ');

    return {
        place_id: prediction.place_id,
        description,
        types: prediction.types ?? [],
        structured_formatting: {
            main_text: mainText,
            secondary_text: secondaryText
        }
    };
};

interface AddressValue {
    street?: string;
    streetnumber?: string;
    city?: string;
    postalcode?: string;
    country?: string;
    longitude?: number;
    latitude?: number;
}

interface AddressSearchModalContentProps {
    onClose: () => void;
    onSelectAddress: (address: AddressValue) => void;
    onManualEntry: () => void;
}

const SearchLoader = () => {
    return (
        <div
            className={cx(
                'tw-flex tw-h-24 tw-w-24 tw-items-center tw-justify-center tw-rounded-xl tw-transition-all tw-duration-300'
            )}
            aria-live="polite"
        >
            <Spinner color="success" size={'xl'} />
        </div>
    );
};

const AddressSearchModalContent = ({ onClose, onSelectAddress, onManualEntry }: AddressSearchModalContentProps) => {
    const api = useApi();
    const [query, setQuery] = useState('');
    const [placePredictions, setPlacePredictions] = useState<Prediction[]>([]);
    const [isPlacePredictionsLoading, setIsPlacePredictionsLoading] = useState(false);
    const [searchHint, setSearchHint] = useState<string | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const hasSearchQuery = query.trim().length > 1;

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    const fetchPredictions = (input: string) =>
        new Promise<Prediction[]>((resolve) => {
            if (!input.trim()) {
                resolve([]);
                return;
            }

            void api({
                url: `${PLACES_BASE}/autocomplete`,
                method: 'GET',
                data: { input },
                muted: true,
                onSuccess: (data: PlacesAutocompleteResponse | Prediction[]) => {
                    const predictions = Array.isArray(data) ? data : (data.predictions ?? []);
                    resolve(
                        predictions
                            .map(normalizePrediction)
                            .filter((prediction): prediction is Prediction => prediction !== null)
                    );
                },
                onError: () => resolve([])
            });
        });

    const fetchPlaceDetails = (placeId: string) =>
        new Promise<AddressComponent[]>((resolve) => {
            void api({
                url: `${PLACES_BASE}/details`,
                method: 'GET',
                data: { place_id: placeId },
                muted: true,
                onSuccess: (data: any) => resolve(data?.result?.address_components ?? []),
                onError: () => resolve([])
            });
        });

    const handleQuery = (text: string) => {
        setQuery(text);
        setSearchHint(null);
        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (text.trim().length <= 1) {
            setPlacePredictions([]);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            setIsPlacePredictionsLoading(true);
            try {
                setPlacePredictions(await fetchPredictions(text));
            } catch {
                setPlacePredictions([]);
            } finally {
                setIsPlacePredictionsLoading(false);
            }
        }, 400);
    };

    const focusSearchInput = () => {
        window.requestAnimationFrame(() => inputRef.current?.focus());
    };

    const prefillStreetForHouseNumber = (prediction: Prediction) => {
        const nextQuery = `${prediction.structured_formatting.main_text} `;
        setQuery(nextQuery);
        setPlacePredictions([]);
        setSearchHint('Bitte ergänze jetzt die Hausnummer und wähle danach die vollständige Adresse aus.');
        focusSearchInput();
    };

    const handleSelect = async (prediction: Prediction) => {
        if (prediction.types.includes('route') && !prediction.types.includes('street_address')) {
            prefillStreetForHouseNumber(prediction);
            return;
        }

        try {
            const components = await fetchPlaceDetails(prediction.place_id);
            if (components.length === 0) return;

            const nextAddress = {
                street: getComponent('route', components),
                streetnumber: getComponent('street_number', components),
                city:
                    getComponent('locality', components) ||
                    getComponent('sublocality', components) ||
                    getComponent('administrative_area_level_1', components),
                postalcode: getComponent('postal_code', components),
                country: getComponent('country', components, true)
            };

            if (!nextAddress.streetnumber) {
                prefillStreetForHouseNumber(prediction);
                return;
            }

            onSelectAddress(nextAddress);
            onClose();
        } catch {
            // keep modal open so the user can retry or switch to manual entry
        }
    };

    return (
        <div className="tw-flex tw-min-h-[50vh] tw-flex-col">
            <Blockquote className="tw-mb-3 tw-flex tw-items-center tw-gap-3">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="tw-text-accents-6" />
                <StyledInput
                    autoFocus
                    bordered
                    fullWidth
                    ref={inputRef}
                    value={query}
                    onChange={(e) => handleQuery(e.target.value)}
                    placeholder="Straße, Hausnummer, Stadt…"
                    className="tw-border-0 tw-bg-transparent tw-text-sm tw-text-accents-9 tw-outline-none placeholder:tw-text-accents-5"
                />
            </Blockquote>

            <div className="tw-flex tw-flex-1 tw-flex-col">
                {searchHint && (
                    <Blockquote color="warning" className="tw-flex tw-gap-4">
                        <FontAwesomeIcon
                            icon={faTriangleExclamation}
                            className="tw-mt-0.5 tw-shrink-0 tw-text-warning"
                        />
                        <p className="tw-m-0 tw-text-sm tw-text-accents-9">{searchHint}</p>
                    </Blockquote>
                )}

                {hasSearchQuery ? (
                    <div className="tw-flex tw-h-full tw-flex-1 tw-flex-col tw-justify-center tw-gap-3">
                        {isPlacePredictionsLoading && placePredictions.length === 0 && (
                            <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">
                                {' '}
                                <SearchLoader />
                            </div>
                        )}

                        {placePredictions.map((prediction) => (
                            <button
                                key={prediction.place_id}
                                type="button"
                                onClick={() => void handleSelect(prediction)}
                                className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-solid tw-border-accents-3 tw-bg-white tw-p-3 tw-text-left tw-transition-colors hover:tw-border-accents-4 hover:tw-bg-accents-0"
                            >
                                <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-accents-2 tw-bg-accents-0">
                                    <FontAwesomeIcon icon={faLocationDot} className="tw-text-accents-6" />
                                </span>
                                <span className="tw-flex tw-min-w-0 tw-flex-col">
                                    <span className="tw-truncate tw-text-sm tw-font-medium tw-text-accents-9">
                                        {prediction.structured_formatting.main_text}
                                    </span>
                                    {!!prediction.structured_formatting.secondary_text && (
                                        <span className="tw-truncate tw-text-xs tw-text-accents-6">
                                            {prediction.structured_formatting.secondary_text}
                                        </span>
                                    )}
                                </span>
                            </button>
                        ))}

                        {!isPlacePredictionsLoading && placePredictions.length === 0 && !searchHint && (
                            <div className="tw-mt-2 tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center">
                                <p className="tw-text-md tw-m-0 tw-text-accents-8">Keine Treffer gefunden.</p>
                                <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-accents-7">
                                    Prüfe die Schreibweise oder nutze die manuelle Eingabe.
                                </p>
                            </div>
                        )}

                        {placePredictions.length > 0 && (
                            <Blockquote className="tw-flex tw-gap-4">
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    className="tw-mt-0.5 tw-shrink-0 tw-text-accents-6"
                                />
                                <p className="tw-m-0 tw-text-xs tw-text-accents-7">
                                    Es werden maximal 5 Treffer angezeigt. Ist deine Adresse nicht dabei, gib mehr
                                    Details ein (z.&nbsp;B. Hausnummer und Ort) oder nutze die manuelle Eingabe.
                                </p>
                            </Blockquote>
                        )}
                    </div>
                ) : (
                    <Blockquote className="tw-flex tw-gap-4">
                        <p className="tw-m-0 tw-text-sm tw-text-accents-8">
                            {searchHint ||
                                'Suche nach einer vollständigen Adresse, um Straße, Hausnummer und Ort direkt zu übernehmen.'}
                        </p>
                    </Blockquote>
                )}

                <div className="tw-mt-4 tw-mt-auto tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-justify-end">
                    <Button type="button" flat color="light" onPress={onClose}>
                        Abbrechen
                    </Button>
                    <Button type="button" color="secondary" onPress={onManualEntry}>
                        Manuell eingeben
                    </Button>
                </div>
            </div>
        </div>
    );
};

const FormAddressSearch = ({ label, name, required = false, inputProps }: Props) => {
    const { control } = useFormContext();
    const {
        field: { value, onBlur, onChange: setAddressValue },
        fieldState: { error }
    } = useController({
        name,
        control,
        rules: {
            validate: (value) => getAddressValidationMessage(required, value)
        }
    });
    const { addModal, removeModal } = useModalManager();

    const currentAddress = value ?? {};
    const errorMessage = error?.message as string | undefined;
    const [inlineManual, setInlineManual] = useState(false);

    const summary = currentAddress.street
        ? [
              [currentAddress.street, currentAddress.streetnumber].filter(Boolean).join(' '),
              [currentAddress.postalcode, currentAddress.city].filter(Boolean).join(' ')
          ]
              .filter(Boolean)
              .join(', ')
        : null;

    const ManualGrid = () => (
        <div className="tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-4">
            <FormInput name={`${name}.street`} label="Straße" fullWidth className="tw-col-span-3 !tw-my-0" />
            <FormInput name={`${name}.streetnumber`} label="Nummer" fullWidth className="!tw-my-0" />
            <FormInput name={`${name}.postalcode`} label="PLZ" fullWidth className="!tw-my-0" />
            <FormInput name={`${name}.city`} label="Stadt" fullWidth className="tw-col-span-3 !tw-my-0" />
        </div>
    );

    const openSearchModal = () => {
        addModal({
            title: 'Adresse suchen',
            size: 50,
            content: (id) => (
                <AddressSearchModalContent
                    onClose={() => removeModal(id)}
                    onSelectAddress={(address) => {
                        setAddressValue(address);
                        setInlineManual(false);
                    }}
                    onManualEntry={() => {
                        setInlineManual(true);
                        removeModal(id);
                    }}
                />
            ),
            show: true
        });
    };

    return (
        <div className="tw-mb-4 tw-flex tw-flex-col tw-gap-2">
            {label && (
                <label
                    className={cx(
                        '!tw-mb-0 tw-pl-1 tw-text-sm tw-font-medium',
                        errorMessage ? 'tw-text-error' : 'tw-text-accents-9'
                    )}
                >
                    {label}
                </label>
            )}

            {/* Trigger */}
            <button
                type="button"
                onClick={openSearchModal}
                onBlur={onBlur}
                className={cx(
                    'tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-bg-white tw-px-4 tw-py-3 tw-text-left tw-transition-colors hover:tw-border-accents-4',
                    errorMessage ? 'tw-border-error' : 'tw-border-accents-3'
                )}
            >
                <FontAwesomeIcon
                    icon={faLocationDot}
                    className={errorMessage ? 'tw-text-error' : 'tw-text-accents-6'}
                />
                <span
                    className={cx(
                        'tw-flex-1 tw-truncate tw-text-sm',
                        summary ? 'tw-text-accents-9' : 'tw-text-accents-6'
                    )}
                >
                    {summary ?? 'Adresse suchen…'}
                </span>
                {summary && <FontAwesomeIcon icon={faPenToSquare} className="tw-text-xs tw-text-accents-5" />}
            </button>

            {/* Inline manual toggle */}
            <Button flat color="secondary" block size="xs" onClick={() => setInlineManual((v) => !v)}>
                <FontAwesomeIcon icon={inlineManual ? faXmark : faPenToSquare} className="tw-text-[10px]" />
                {inlineManual ? 'Manuelle Eingabe ausblenden' : 'Adresse manuell eingeben'}
            </Button>

            {inlineManual && (
                <div className="tw-card-bordered tw-border-accents-3 tw-p-3">
                    <ManualGrid />
                </div>
            )}

            {errorMessage && <p className="tw-m-0 tw-pl-1 tw-text-xs tw-text-error">{errorMessage}</p>}
            {!errorMessage && inputProps?.helperText && (
                <p className="tw-m-0 tw-pl-1 tw-text-xs tw-text-accents-6">{inputProps.helperText}</p>
            )}
        </div>
    );
};

export default FormAddressSearch;
